// node modules
import React, { FC } from 'react';
import { observer } from 'mobx-react';
// ui-library
import { TableRow, useThemeDetector, TableCell, View } from '@upstox/upstox-ui';
import TVOptionChainRow from '@upstox/upstox-ui/component/partial/TVOptionChainRow';
// modules
import { useStore } from 'modules/Stores';
// constants
import { DATA_SETTINGS } from 'modules/OptionChain/consts';
// utils
import { getHeaderColumns } from '../utils';
// components
import OptionChainIVCell from './OptionChainIVCell';
import OptionChainGammaCell from './OptionChainGammaCell';
import OptionChainDeltaCell from './OptionChainDeltaCell';
import OptionChainBidCell from './OptionChainBidCell';
import OptionChainAskCell from './OptionChainAskCell';
import OptionChainLtpCell from './OptionChainLtpCell';
import OptionChainOICell from './OptionChainOICell';
import OptionChainOIChange from './OptionChainOIChange';
import OptionChainThetaCell from './OptionChainThetaCell';
import OptionChainVegaCell from './OptionChainVegaCell';
import OptionChainVolumeCell from './OptionChainVolumeCell';
import OptionChainOIBarCell from './OptionChainOIBarCell';
import HoverCell from './HoverCell';
// styles
import * as css from './styles';
// typings
import { Props } from './typings';

const OptionChainRow: FC<Props> = props => {
  const { dataId, item, type } = props;
  const { theme } = useStore('AppStore');
  const autoTheme = useThemeDetector();
  const currentTheme = theme === 'auto' ? autoTheme : theme;
  const { tableSettings, loading } = useStore('OptionChainStore');
  const {
    columnSettings: {
      columns: { showVolume, showIV, showOIChange, showAsk, showBid },
      greeks: { showDelta, showGamma, showTheta, showVega },
    },
    dataSettings: { changeIn },
  } = tableSettings;

  if (!item) return null;

  const columnProps = {
    showAsk,
    showBid,
    showIV,
    showLTP: true,
    showOI: true,
    showOIChange,
    showChangeInPercent: changeIn === DATA_SETTINGS.changePer,
    showVolume,
    showDelta,
    showTheta,
    showGamma,
    showVega,
    customActionsCell: true,
  };

  const headerColumns = getHeaderColumns(tableSettings, type);
  const totalNoOfColumns = headerColumns.length + 1;

  const getRow = (): TVOptionChainRowProps => {
    const { instrumentKey } = item;
    const call = type === 'CE';
    const highlight = item.rowType === 'itm';
    const atm = item.rowType === 'atm';

    return {
      ltp: (
        <React.Fragment>
          <OptionChainLtpCell
            instrumentKey={instrumentKey}
            isCall={call}
            highlight={highlight}
          />
          {!loading && (
            <OptionChainOIBarCell instrumentKey={instrumentKey} isCall={call} />
          )}
        </React.Fragment>
      ),
      bid: (
        <OptionChainBidCell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      ask: (
        <OptionChainAskCell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      iv: (
        <OptionChainIVCell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      volume: (
        <OptionChainVolumeCell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      oi: (
        <OptionChainOICell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      oiChange: (
        <OptionChainOIChange
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      delta: (
        <OptionChainDeltaCell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      theta: (
        <OptionChainThetaCell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      gamma: (
        <OptionChainGammaCell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      vega: (
        <OptionChainVegaCell
          instrumentKey={instrumentKey}
          isCall={call}
          highlight={highlight}
        />
      ),
      selectedType: 'none',
      highlight,
      atm,
    };
  };

  return (
    <TableRow
      css={
        item.rowType === 'spot'
          ? css.separatorRow
          : css.rowContainer
      }
      cssProps={{
        atm: item.rowType === 'atm',
        isDarkTheme: currentTheme === 'dark',
      }}
      data-id={`${dataId}-${item.strike}`}
    >
      {item.rowType !== 'spot' ? (
        <React.Fragment>
          {type === 'PE' && (
            <HoverCell
              instrumentKey={item.instrumentKey}
              exchangeToken={item.instrumentKey}
              isCall={false}
              rowType={item.rowType}
            />
          )}
          <TVOptionChainRow
            {...getRow()}
            {...columnProps}
            call={type === 'CE'}
          />
          {type === 'CE' && (
            <HoverCell
              exchangeToken={item.exchangeToken}
              instrumentKey={item.instrumentKey}
              isCall
              rowType={item.rowType}
            />
          )}
        </React.Fragment>
      ) : (
        <TableCell colSpan={totalNoOfColumns} css={css.separatorCell}>
          <View css={css.separator} />
        </TableCell>
      )}
    </TableRow>
  );
};

export default observer(OptionChainRow);
